// Notification bell + panel — reusable across intranet and editor.
// Reads `root.notifications` array (caller populates via listNotifications).
// Writes go through collab-features/notifications/queries directly so
// the components are self-sufficient (no `el.call` dependency on a
// specific app's function registry).

import { markRead, markAllRead } from './queries.js'

const _unread = (root) => (root?.notifications || []).filter((n) => !n.read).length

const ICON_BY_TYPE = {
  mention: 'chat',
  ticket_assign: 'check',
  calendar_invite: 'date',
  doc_share: 'document',
  system: 'settings'
}

const COLOR_BY_TYPE = {
  mention: 'blue',
  ticket_assign: 'green',
  calendar_invite: 'purple',
  doc_share: 'orange',
  system: 'caption'
}

const _timeAgo = (iso) => {
  if (!iso) return ''
  const ms = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(ms / 60000)
  if (mins < 1) return 'now'
  if (mins < 60) return `${mins}m`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h`
  return `${Math.floor(hrs / 24)}d`
}

// Optimistic write helper — updates root state instantly, fires server
// write in the background. Errors are logged, not surfaced.
const _markReadOptimistic = async (root, id) => {
  if (!root || !id) return
  root.update({
    notifications: (root.notifications || []).map((n) =>
      n.id === id ? { ...n, read: true } : n
    )
  })
  const { error } = await markRead(id)
  if (error) console.warn('[notifications] markRead failed:', error?.message || error)
}

const _markAllReadOptimistic = async (root) => {
  if (!root?.currentUserEmail) return
  root.update({
    notifications: (root.notifications || []).map((n) => ({ ...n, read: true }))
  })
  const { error } = await markAllRead(root.currentUserEmail)
  if (error) console.warn('[notifications] markAllRead failed:', error?.message || error)
}

export const NotificationBell = {
  tag: 'button',
  type: 'button',
  title: 'Notifications',
  position: 'relative',
  padding: 'Z',
  round: 'Z',
  cursor: 'pointer',
  borderWidth: '0',
  background: 'transparent',
  color: 'caption',
  ':hover': { background: 'overlay', color: 'title' },

  onClick: (e, el, s) => {
    e.stopPropagation()
    s.root.update({ notificationsPanelOpen: !s.root.notificationsPanelOpen })
  },

  Icon: { icon: 'bell', boxSize: 'A A' },

  UnreadBadge: {
    if: (el, s) => _unread(s.root) > 0,
    position: 'absolute',
    top: '0',
    right: '0',
    minWidth: 'Z',
    height: 'Z',
    padding: '0 V',
    round: 'C',
    background: 'red',
    color: 'white',
    fontSize: 'W',
    fontWeight: '700',
    lineHeight: '1',
    align: 'center center',
    pointerEvents: 'none',
    text: (el, s) => {
      const n = _unread(s.root)
      return n > 99 ? '99+' : String(n)
    }
  }
}

export const NotificationPanel = {
  display: (el, s) => (s.root?.notificationsPanelOpen ? 'flex' : 'none'),
  position: 'absolute',
  top: 'B1',
  right: '0',
  width: '360px',
  maxHeight: '480px',
  overflow: 'hidden',
  padding: '0',
  round: 'Z1',
  background: 'document',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'line',
  boxShadow: '0 16px 48px -8px var(--color-shadow)',
  zIndex: '3000',
  flow: 'y',

  onRender: (el) => {
    const handler = (e) => {
      if (!el.node?.contains(e.target) && !el.parent?.node?.contains(e.target)) {
        el.state.root.update({ notificationsPanelOpen: false })
        document.removeEventListener('mousedown', handler)
      }
    }
    setTimeout(() => document.addEventListener('mousedown', handler), 0)
    el._closeHandler = handler
  },
  onRemove: (el) => {
    if (el._closeHandler) document.removeEventListener('mousedown', el._closeHandler)
  },

  Header: {
    flow: 'x',
    align: 'center space-between',
    padding: 'Z1 A',
    borderBottom: '1px solid var(--color-line)',

    Title: {
      tag: 'span',
      text: 'Notifications',
      fontWeight: '700',
      fontSize: 'Z1',
      color: 'title'
    },

    MarkAllBtn: {
      tag: 'button',
      type: 'button',
      text: 'Mark all read',
      background: 'none',
      borderWidth: '0',
      color: 'blue',
      fontSize: 'Y2',
      fontWeight: '600',
      cursor: 'pointer',
      padding: '0',
      if: (el, s) => _unread(s.root) > 0,
      onClick: (e, el, s) => {
        e.stopPropagation()
        _markAllReadOptimistic(s.root)
      }
    }
  },

  List: {
    overflow: 'auto',
    maxHeight: '420px',
    flow: 'y',

    Empty: {
      if: (el, s) => !(s.root?.notifications || []).length,
      padding: 'C A',
      textAlign: 'center',
      color: 'caption',
      fontSize: 'Z1',
      text: 'No notifications yet'
    },

    childrenAs: 'state',
    children: (el, s) => (s.root?.notifications || []).slice(0, 30),
    childExtends: {
      flow: 'x',
      gap: 'Z',
      padding: 'Z1 A',
      cursor: 'pointer',
      borderBottom: '1px solid var(--color-line)',
      background: (el, s) => (s.read ? 'transparent' : 'overlay'),
      ':hover': { background: 'overlayStrong' },

      onClick: (e, el, s) => {
        e.stopPropagation()
        _markReadOptimistic(s.root, s.id)
        if (s.href) {
          s.root.update({ notificationsPanelOpen: false })
          let rootEl = el
          while (rootEl && !rootEl.routes) rootEl = rootEl.parent
          if (rootEl) el.router(s.href, rootEl)
        }
      },

      UnreadDot: {
        if: (el, s) => !s.read,
        width: 'V',
        height: 'V',
        round: '50%',
        background: 'blue',
        flexShrink: '0',
        marginTop: 'Z'
      },

      TypeIcon: {
        Icon: {
          icon: (el, s) => ICON_BY_TYPE[s.parent?.state?.type] || 'star',
          boxSize: 'A',
          color: (el, s) => COLOR_BY_TYPE[s.parent?.state?.type] || 'caption'
        }
      },

      Body: {
        flex: '1',
        flow: 'y',
        gap: 'W',
        minWidth: '0',

        NTitle: {
          tag: 'span',
          fontSize: 'Z',
          fontWeight: '600',
          color: 'title',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          text: (el, s) => s.parent?.state?.title || ''
        },

        NBody: {
          if: (el, s) => s.parent?.state?.body,
          tag: 'span',
          fontSize: 'Y2',
          color: 'caption',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          text: (el, s) => s.parent?.state?.body || ''
        }
      },

      Time: {
        tag: 'span',
        fontSize: 'Y2',
        color: 'caption',
        flexShrink: '0',
        whiteSpace: 'nowrap',
        text: (el, s) => _timeAgo(s.createdAt || s.created_at)
      }
    }
  }
}

// Self-loading wrapper — primes `root.notifications` the first time
// it mounts (onRender). Apps that already populate notifications
// elsewhere can skip this and mount NotificationBell + Panel directly.
export const NotificationWrapper = {
  position: 'relative',

  onRender: async (el, s) => {
    if (el._loaded) return
    el._loaded = true
    const email = s.root?.currentUserEmail
    if (!email) return
    const { listNotifications } = await import('./queries.js')
    const { data } = await listNotifications({ recipientEmail: email, limit: 50 })
    const rows = (data || []).map((n) => ({
      ...n,
      createdAt: n.created_at,
      read: !!n.read
    }))
    s.root.update({ notifications: rows })
  },

  NotificationBell: {},
  NotificationPanel: {}
}
